import { atom, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useCallback } from 'react';
import { LoginUserRequest, LoginUserResponse } from '../../functions/api/v1/users/login/type';
import { RegisterUserRequest, RegisterUserResponse } from '../../functions/api/v1/users/register/type';
import { ChallengeUserResponse } from '../../functions/api/v1/users/challenge/type';

// Base URL取得関数
const getBaseUrl = () => {
    // ブラウザ環境でない場合は環境変数のURLを使用
    if (typeof window === 'undefined') {
        return import.meta.env.VITE_API_BASE_URL;
    }
    // 現在のURLのドメインまでを取得
    const url = new URL(window.location.href);
    return `${url.protocol}//${url.host}`;
};

// Base atoms
export const accessTokenAtom = atomWithStorage<string>('access-token', '', undefined, {
    getOnInit: true,
});
export const userAtom = atom<ChallengeUserResponse | null>(null);
export const currentPathAtom = atom<string>('');

export const globalActionsAtom = atom(null, async (get, set, action: {
    type: 'REGISTER' | 'LOGIN' | 'CHALLENGE';
    payload?: RegisterUserRequest | LoginUserRequest;
}) => {
    const baseUrl = getBaseUrl();

    switch (action.type) {
        case 'REGISTER': {
            const payload = action.payload as RegisterUserRequest;
            const userRegisterResponse = await
                fetch(`${baseUrl}/api/v1/users/register`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            if (!userRegisterResponse.ok) {
                throw userRegisterResponse;
            }
            const userRegisterResponseJson = await userRegisterResponse.json() as RegisterUserResponse;
            set(accessTokenAtom, userRegisterResponseJson.accessToken);
            break;
        }
        case 'LOGIN': {
            const payload = action.payload as LoginUserRequest;
            const userLoginResponse = await
                fetch(`${baseUrl}/api/v1/users/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            if (!userLoginResponse.ok) {
                throw userLoginResponse;
            }
            const userLoginResponseJson = await userLoginResponse.json() as LoginUserResponse;
            set(accessTokenAtom, userLoginResponseJson.accessToken);
            break;
        }
        case 'CHALLENGE': {
            const accessToken = get(accessTokenAtom);
            if (!accessToken) {
                set(userAtom, null);
                return;
            }
            try {
                const userChallengeResponse = await
                    fetch(`${baseUrl}/api/v1/users/challenge`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${accessToken}`,
                        },
                    });
                if (!userChallengeResponse.ok) {
                    if (userChallengeResponse.status === 401) {
                        // トークンが無効または有効期限切れの場合
                        set(accessTokenAtom, '');
                        set(userAtom, null);
                        return;
                    }
                    throw new Error('Challenge API error');
                }
                const userChallengeResponseJson = await userChallengeResponse.json() as ChallengeUserResponse;
                set(userAtom, userChallengeResponseJson);
            } catch (error: unknown) {
                console.error('Challenge API error:', error);
                set(accessTokenAtom, '');
                set(userAtom, null);
            }
            break;
        }
    }
});

// Custom hooks
export const useGlobalActions = () => {
    const [, dispatch] = useAtom(globalActionsAtom);

    return {
        userRegister: useCallback(async (request: RegisterUserRequest) => {
            await dispatch({
                type: 'REGISTER',
                payload: request
            });
        }, [dispatch]),
        userLogin: useCallback(async (email: string, password: string) => {
            await dispatch({
                type: 'LOGIN',
                payload: {
                    email: email,
                    password: password
                }
            });
        }, [dispatch]),
        userChallenge: useCallback(async () => {
            await dispatch({
                type: 'CHALLENGE'
            });
        }, [dispatch]),
    };
};